import React, { useState, useCallback, useMemo, useEffect } from "react";
import { IconButton, InputAdornment, Stack } from "@mui/material";
import i18n from "i18n";
import { useTranslation } from "react-i18next";
import { SearchIcon } from "components/icon";
import { ClearRounded as ClearRoundedIcon } from "components/icon/mui_icon";
import { useEffectAfterMount, useDebounce, useQueryParam } from "hooks";
import { setParamsKeyName } from "utils/setParamsKeyName";
import FormInputUI from "./UI/FormInputUI";

/**
 * SearchInput Component
 * ---------------------
 * A reusable search input that:
 * - Synchronizes its value with URL query parameters
 * - Debounces input to prevent frequent updates
 * - Provides a clear button to reset the input
 *
 * Props:
 * - placeHolder (string): Placeholder text for the input (default: "Search")
 */
const SearchInput = ({ placeHolder = i18n.t("common:search"), isTableView = true }) => {
  const { t } = useTranslation("form");
  // Access URL query parameters
  const keyName = useMemo(
    () => setParamsKeyName("search", isTableView),
    [isTableView],
  );
  const pageName = useMemo(
    () => setParamsKeyName("page", isTableView),
    [isTableView],
  );

  const [search, setSearch] = useQueryParam(keyName, "");

  // Local state to control input value
  const [value, setValue] = useState(search);

  // Debounce input value to prevent excessive URL updates
  const debouncedValue = useDebounce(value, 300);

  /**
   * Effect: Update URL param whenever debounced value changes
   * - Encodes the new search value while preserving other URL params
   */
  useEffectAfterMount(() => {
    setSearch({ [keyName]: debouncedValue, [pageName]: 1 });
  }, [debouncedValue, keyName, pageName]);

  /**
   * Handlers
   */
  // Updates input state on typing
  const updateValue = useCallback((e) => setValue(e.target.value), []);

  // Clears input state when clear button is clicked
  const clearValue = useCallback(() => setValue(""), []);

  // ✅ Sync external → internal
  useEffect(() => {
    setValue(search || "");
  }, [search]);

  // End adornment: clear button (shown only when input has value)
  const endAdornment = useMemo(
    () =>
      value ? (
        <InputAdornment position="end">
          <IconButton onClick={clearValue} aria-label={t("resetSearch")}>
            <ClearRoundedIcon />
          </IconButton>
        </InputAdornment>
      ) : null,
    [value, clearValue, t],
  );

  // Render the input field inside a stack
  return (
    <Stack sx={{ alignItems: "flex-start", m: 0, maxWidth: 320, width: "100%", height: 40 }}>
      <FormInputUI
        id="search"
        value={value}
        onChange={updateValue}
        placeholder={placeHolder}
        startAdornment={<SearchIcon sx={{ fontSize: "1.125rem" }} />}
        endAdornment={endAdornment}
        inputSx={{ height: 40 }}
      />
    </Stack>
  );
};

// Memoized to prevent unnecessary re-renders when props do not change
export default React.memo(SearchInput);
